import { Trans, t } from '@grafana/i18n';
import React, { memo, RefCallback, useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useMeasure, usePrevious } from 'react-use';

// @ts-ignore
import {
  AlertState,
  DataFrame,
  GrafanaTheme2,
  PanelData,
  PanelPlugin,
  PanelProps,
  PluginContextProvider,
  PluginType,
  QueryResultMetaNotice,
  renderMarkdown,
  SetPanelAttentionEvent,
} from '@grafana/data';

import { getAppEvents } from '@grafana/runtime';
import { PanelChrome, ErrorBoundaryAlert, PanelContextProvider, Tooltip, useStyles2, Icon } from '@grafana/ui';

import { sceneGraph } from '../../core/sceneGraph';
import { isSceneObject, SceneComponentProps, SceneLayout, SceneObject } from '../../core/types';

import { VizPanel } from './VizPanel';
import { css, cx } from '@emotion/css';
import { debounce } from 'lodash';
import { VizPanelSeriesLimit } from './VizPanelSeriesLimit';
import { useLazyLoaderIsInView } from '../layout/LazyLoader';

export function VizPanelRenderer({ model }: SceneComponentProps<VizPanel>) {
  const {
    title,
    options,
    fieldConfig,
    _pluginLoadError,
    displayMode,
    hoverHeader,
    showMenuAlways,
    hoverHeaderOffset,
    menu,
    headerActions,
    subHeader,
    subtitle,
    titleItems,
    seriesLimit,
    seriesLimitShowAll,
    description,
    collapsible,
    collapsed,
    _renderCounter = 0,
    _UNSAFE_clearPreviousFieldValues = false,
  } = model.useState();
  let [ref, { width, height }] = useMeasure();
  const appEvents = useMemo(() => getAppEvents(), []);

  const setPanelAttention = useCallback(() => {
    if (model.state.key) {
      appEvents.publish(new SetPanelAttentionEvent({ panelId: model.getPathId() }));
    }
  }, [model, appEvents]);

  const debouncedMouseMove = useMemo(
    () => debounce(setPanelAttention, 100, { leading: true, trailing: false }),
    [setPanelAttention]
  );

  // S3.0 RENDER TRACKING: Simple timing for performance measurement
  const profiler = useMemo(() => model.getProfiler(), [model]);

  // Capture render start time immediately when component function runs
  const currentRenderStart = performance.now();

  const endRenderCallbackRef = React.useRef<((endTimestamp: number, duration: number) => void) | null>(null);

  useLayoutEffect(() => {
    if (profiler) {
      const callback = profiler.onSimpleRenderStart(currentRenderStart);
      endRenderCallbackRef.current = callback || null;
    }
  });

  const subtitleContent = useMemo(() => {
    if (subtitle) {
      return renderMarkdown(model.interpolate(subtitle, undefined, 'text'));
    }
    return undefined;
  }, [subtitle, model]);

  // Use useEffect (after DOM updates) to measure complete render cycle timing
  useEffect(() => {
    if (endRenderCallbackRef.current) {
      const timestamp = performance.now();
      // Measure from component start to after DOM updates AND effects (complete render cycle)
      const duration = timestamp - currentRenderStart;
      endRenderCallbackRef.current(timestamp, duration);
      endRenderCallbackRef.current = null; // Clear callback after use
    }
  });

  const plugin = model.getPlugin() ?? getLoadingPlugin();

  const { dragClass, dragClassCancel } = getDragClasses(model);
  const dragHooks = getDragHooks(model);
  const dataObject = sceneGraph.getData(model);

  const rawData = dataObject.useState();

  const { series, annotations } = _UNSAFE_clearPreviousFieldValues ? rawData.data ?? {} : {};
  useClearPreviousData(series);
  useClearPreviousData(annotations);

  const dataWithSeriesLimit = useDataWithSeriesLimit(rawData.data, seriesLimit, seriesLimitShowAll);
  const dataWithFieldConfig = model.applyFieldConfig(dataWithSeriesLimit);
  const sceneTimeRange = sceneGraph.getTimeRange(model);
  const timeZone = sceneTimeRange.getTimeZone();
  const timeRange = model.getTimeRange(dataWithFieldConfig);

  // Switch to manual query execution if the panel is outside viewport
  const isInView = useLazyLoaderIsInView();
  useEffect(() => {
    if (dataObject.isInViewChanged) {
      dataObject.isInViewChanged(isInView);
    }
  }, [isInView, dataObject]);

  // Interpolate title
  const titleInterpolated = model.interpolate(title, undefined, 'text');
  const alertStateStyles = useStyles2(getAlertStateStyles);

  if (!plugin.panel) {
    return (
      <div>
        <Trans i18nKey="grafana-scenes.components.viz-panel-renderer.panel-plugin-has-no-panel-component">
          Panel plugin has no panel component
        </Trans>
      </div>
    );
  }

  const PanelComponent = plugin.panel;

  // If we have a query runner on our level inform it of the container width (used to set auto max data points)
  if (dataObject && dataObject.setContainerWidth) {
    dataObject.setContainerWidth(Math.round(width));
  }

  let subHeaderElement: React.ReactNode[] = [];

  if (subHeader) {
    if (Array.isArray(subHeader)) {
      subHeaderElement = subHeaderElement.concat(
        subHeader.map((subHeaderItem) => {
          return <subHeaderItem.Component model={subHeaderItem} key={`${subHeaderItem.state.key}`} />;
        })
      );
    } else if (isSceneObject(subHeader)) {
      subHeaderElement.push(<subHeader.Component model={subHeader} key={`${subHeader.state.key}`} />);
    } else {
      subHeaderElement.push(subHeader);
    }
  }

  let titleItemsElement: React.ReactNode[] = [];

  if (titleItems) {
    if (Array.isArray(titleItems)) {
      titleItemsElement = titleItemsElement.concat(
        titleItems.map((titleItem) => {
          return <titleItem.Component model={titleItem} key={`${titleItem.state.key}`} />;
        })
      );
    } else if (isSceneObject(titleItems)) {
      titleItemsElement.push(<titleItems.Component model={titleItems} />);
    } else {
      titleItemsElement.push(titleItems);
    }
  }

  if (seriesLimit) {
    titleItemsElement.push(
      <VizPanelSeriesLimit
        key="series-limit"
        data={rawData.data}
        seriesLimit={seriesLimit}
        showAll={seriesLimitShowAll}
        onShowAllSeries={() => model.setState({ seriesLimitShowAll: !seriesLimitShowAll })}
      />
    );
  }

  // If we have local time range show that in panel header
  if (model.state.$timeRange) {
    titleItemsElement.push(<model.state.$timeRange.Component model={model.state.$timeRange} key={model.state.key} />);
  }

  if (dataWithFieldConfig.alertState) {
    titleItemsElement.push(
      <Tooltip content={dataWithFieldConfig.alertState.state ?? 'unknown'} key={`alert-states-icon-${model.state.key}`}>
        <PanelChrome.TitleItem
          className={cx({
            [alertStateStyles.ok]: dataWithFieldConfig.alertState.state === AlertState.OK,
            [alertStateStyles.pending]: dataWithFieldConfig.alertState.state === AlertState.Pending,
            [alertStateStyles.alerting]: dataWithFieldConfig.alertState.state === AlertState.Alerting,
          })}
        >
          <Icon
            name={dataWithFieldConfig.alertState.state === 'alerting' ? 'heart-break' : 'heart'}
            className="panel-alert-icon"
            size="md"
          />
        </PanelChrome.TitleItem>
      </Tooltip>
    );
  }

  let panelMenu;
  if (menu) {
    panelMenu = <menu.Component model={menu} />;
  }

  let actionsElement: React.ReactNode | undefined;

  if (headerActions) {
    if (Array.isArray(headerActions)) {
      actionsElement = (
        <>
          {headerActions.map((action) => {
            return <action.Component model={action} key={`${action.state.key}`} />;
          })}
        </>
      );
    } else if (isSceneObject(headerActions)) {
      actionsElement = <headerActions.Component model={headerActions} />;
    } else {
      actionsElement = headerActions;
    }
  }

  // Data is always returned. For non-data panels, empty PanelData is returned.
  const data = dataWithFieldConfig!;

  const isReadyToRender = dataObject.isDataReadyToDisplay ? dataObject.isDataReadyToDisplay() : true;

  const context = model.getPanelContext();
  // The host (e.g. dashboard) only provides an inspector opener when the new panel errors UI
  // is enabled. Use its presence to decide between the new errors/notices popover and the
  // legacy single status message.
  // @ts-expect-error onOpenInspector is added in a newer @grafana/ui version
  const showNewPanelErrorsUI = Boolean(context.onOpenInspector);
  const panelId = model.getLegacyPanelId();
  const outdatedPluginError = t(
    'grafana-scenes.components.viz-panel-renderer.outdated-plugin-error',
    'An unexpected error occurred. Updating the "{{pluginName}}" plugin to the latest version may fix the problem.',
    { pluginName: plugin.meta.name }
  );

  return (
    <div className={relativeWrapper}>
      <div
        ref={ref as RefCallback<HTMLDivElement>}
        className={absoluteWrapper}
        data-viz-panel-key={model.state.key}
        data-viz-panel-id={model.getPathId()}
      >
        <PanelChrome
          title={titleInterpolated}
          description={description?.trim() ? model.getDescription : undefined}
          // @ts-expect-error remove this on next grafana/ui update
          subtitle={subtitleContent}
          loadingState={data.state}
          statusMessage={getChromeStatusMessage(data, _pluginLoadError)}
          statusItems={showNewPanelErrorsUI ? getChromeStatusItems(data, _pluginLoadError) : undefined}
          statusMessageOnClick={() => {
            // Report the interaction for analytics, then let the container (e.g. dashboard) open the inspector.
            model.onStatusMessageClick();
            // @ts-expect-error onOpenInspector is added in a newer @grafana/ui version
            context.onOpenInspector?.();
          }}
          width={width === 0 ? undefined : width}
          height={height === 0 ? undefined : height}
          selectionId={model.state.key}
          displayMode={displayMode}
          titleItems={titleItemsElement.length > 0 ? titleItemsElement : undefined}
          dragClass={dragClass}
          actions={actionsElement}
          dragClassCancel={dragClassCancel}
          padding={plugin.noPadding ? 'none' : 'md'}
          menu={panelMenu}
          onCancelQuery={model.onCancelQuery}
          onFocus={setPanelAttention}
          onMouseEnter={setPanelAttention}
          onMouseMove={debouncedMouseMove}
          subHeaderContent={subHeaderElement.length ? subHeaderElement : undefined}
          onDragStart={(e: React.PointerEvent) => {
            dragHooks.onDragStart?.(e, model);
          }}
          showMenuAlways={showMenuAlways}
          {...(collapsible
            ? {
                collapsible: Boolean(collapsible),
                collapsed,
                onToggleCollapse: model.onToggleCollapse,
              }
            : { hoverHeader, hoverHeaderOffset })}
        >
          {(innerWidth, innerHeight) => {
            if (innerWidth === 0 || innerHeight === 0) {
              return null;
            }

            return (
              <ErrorBoundaryAlert
                title={plugin.meta.hasUpdate ? outdatedPluginError : undefined}
                dependencies={[plugin, data]}
              >
                <PluginContextProvider meta={plugin.meta}>
                  <PanelContextProvider value={context}>
                    {isReadyToRender && (
                      <PanelComponent
                        id={panelId}
                        data={data}
                        title={title}
                        timeRange={timeRange}
                        timeZone={timeZone}
                        options={options}
                        fieldConfig={fieldConfig}
                        transparent={displayMode === 'transparent'}
                        width={innerWidth}
                        height={innerHeight}
                        renderCounter={_renderCounter}
                        replaceVariables={model.interpolate}
                        onOptionsChange={model.onOptionsChange}
                        onFieldConfigChange={model.onFieldConfigChange}
                        onChangeTimeRange={model.onTimeRangeChange}
                        eventBus={context.eventBus}
                      />
                    )}
                  </PanelContextProvider>
                </PluginContextProvider>
              </ErrorBoundaryAlert>
            );
          }}
        </PanelChrome>
      </div>
    </div>
  );
}

function useClearPreviousData(data?: DataFrame[]) {
  // this holds all value arrays from all series or anno frames
  // so we can empty any previous ones that no longer appear in current data
  // why? because React fiber: https://github.com/facebook/react/issues/36176
  const prevVals = useRef<Set<any[]>>();
  const currVals = useRef<Set<any[]>>();
  prevVals.current ??= new Set();
  currVals.current ??= new Set();

  const currFrames = data;
  const prevFrames = usePrevious(currFrames);

  if (currFrames != null && currFrames !== prevFrames) {
    // populate new
    currVals.current.clear();

    for (let i = 0; i < currFrames.length; i++) {
      // skip legacy CircularDataFrame streaming frames
      if ('appendRow' in currFrames[i]) {
        continue;
      }

      let fields = currFrames[i].fields;

      for (let j = 0; j < fields.length; j++) {
        currVals.current.add(fields[j].values);
      }
    }

    // empty out all prev not seen in new
    prevVals.current.forEach((vals) => {
      if (!currVals.current!.has(vals)) {
        vals.length = 0;
      }
    });
    prevVals.current.clear();
    prevVals.current = new Set(currVals.current);
  }
}

function useDataWithSeriesLimit(data: PanelData | undefined, seriesLimit?: number, showAllSeries?: boolean) {
  return useMemo(() => {
    if (!data?.series || !seriesLimit || showAllSeries) {
      return data;
    }

    return {
      ...data,
      series: data.series.slice(0, seriesLimit),
    };
  }, [data, seriesLimit, showAllSeries]);
}

function getDragClasses(panel: VizPanel) {
  const parentLayout = sceneGraph.getLayout(panel);
  const isDraggable = parentLayout?.isDraggable();

  if (!parentLayout || !isDraggable || itemDraggingDisabled(panel, parentLayout)) {
    return { dragClass: '', dragClassCancel: '' };
  }

  return { dragClass: parentLayout.getDragClass?.(), dragClassCancel: parentLayout?.getDragClassCancel?.() };
}

function getDragHooks(panel: VizPanel) {
  const parentLayout = sceneGraph.getLayout(panel);
  return parentLayout?.getDragHooks?.() ?? {};
}

/**
 * Walks up the parent chain until it hits the layout object, trying to find the closest SceneGridItemLike ancestor.
 * It is not always the direct parent, because the VizPanel can be wrapped in other objects.
 */
function itemDraggingDisabled(item: SceneObject, layout: SceneLayout) {
  let obj: SceneObject | undefined = item;

  while (obj && obj !== layout) {
    if ('isDraggable' in obj.state && obj.state.isDraggable === false) {
      return true;
    }

    if ('repeatSourceKey' in obj.state && obj.state.repeatSourceKey) {
      return true;
    }

    obj = obj.parent;
  }

  return false;
}

function getChromeStatusMessage(data: PanelData, pluginLoadingError: string | undefined) {
  if (pluginLoadingError) {
    return pluginLoadingError;
  }

  let message = data.error ? data.error.message : undefined;

  // Handling multiple errors with a single string until we integrate VizPanel with inspector
  if (data.errors) {
    message = data.errors.map((e) => e.message).join(', ');
  }
  return message;
}

type PanelStatusSeverity = QueryResultMetaNotice['severity'];

interface PanelStatusItem {
  severity: PanelStatusSeverity;
  text: string;
}

/**
 * Builds the combined list of query errors and result notices shown in the panel header
 * status popover. Errors come from the query response, notices from the frame metadata.
 */
function getChromeStatusItems(data: PanelData, pluginLoadingError: string | undefined): PanelStatusItem[] {
  const items: PanelStatusItem[] = [];

  if (pluginLoadingError) {
    items.push({ severity: 'error', text: pluginLoadingError });
  }

  const errors = data.errors ?? (data.error ? [data.error] : []);
  for (const error of errors) {
    items.push({ severity: 'error', text: error.message ?? 'Query error' });
  }

  const seen = new Set<string>();
  for (const frame of data.series ?? []) {
    for (const notice of frame.meta?.notices ?? []) {
      const severity: PanelStatusSeverity = notice.severity ?? 'info';
      const key = `${severity}:${notice.text}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      items.push({ severity, text: notice.text });
    }
  }

  return items;
}

const relativeWrapper = css({
  position: 'relative',
  width: '100%',
  height: '100%',
});

/**
 * Sadly this this absolute wrapper is needed for the panel to adopt smaller sizes.
 * The combo of useMeasure and PanelChrome makes the panel take up the width it get's but that makes it impossible to
 * Then adapt to smaller space (say resizing the browser window or undocking menu).
 */
const absoluteWrapper = css({
  position: 'absolute',
  width: '100%',
  height: '100%',
});

const getAlertStateStyles = (theme: GrafanaTheme2) => {
  return {
    ok: css({
      color: theme.colors.success.text,
    }),
    pending: css({
      color: theme.colors.warning.text,
    }),
    alerting: css({
      color: theme.colors.error.text,
    }),
  };
};

let loadingPluginInstance: PanelPlugin | null = null;

export function getLoadingPlugin(): PanelPlugin {
  if (loadingPluginInstance) {
    return loadingPluginInstance;
  }

  const LoadingPluginComp = memo<PanelProps>(() => {
    return (
      <div>
        <Trans i18nKey="grafana-scenes.components.viz-panel-renderer.loading-plugin-panel">
          Loading plugin panel...
        </Trans>
      </div>
    );
  });

  LoadingPluginComp.displayName = 'LoadingPlugin';

  loadingPluginInstance = new PanelPlugin(LoadingPluginComp);

  loadingPluginInstance.meta = {
    id: 'loading-plugin',
    name: 'Loading Plugin',
    sort: 100,
    type: PluginType.panel,
    module: '',
    baseUrl: '',
    info: {
      author: {
        name: '',
      },
      description: '',
      links: [],
      logos: {
        large: '',
        small: '',
      },
      screenshots: [],
      updated: '',
      version: '',
    },
  };

  return loadingPluginInstance;
}
